import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '../context/AuthContext'
import {
  FaStar,
  FaCalendar,
  FaUsers,
  FaDollarSign,
  FaCheck,
  FaClock,
  FaMapMarkerAlt,
} from 'react-icons/fa'
import serviceService from '../services/serviceService'
import bookingService from '../services/bookingService'
import Loader from '../components/Loader'
import { formatPrice } from '../utils/helpers'
import { calculateBookingPrice, calculateBookingDays } from '../utils/bookingCalculations'
import SEO from '../utils/SEO'
import { trackPurchase } from '../utils/analytics'
import { validatePhone } from '../utils/validators'
import Button from '../components/ui/Button'
import PolicyDisplay from '../components/PolicyDisplay'
import cancellationPolicyService from '../services/cancellationPolicyService'

const ServiceBooking = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [service, setService] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [serviceRemark, setServiceRemark] = useState('')
  const [availabilityInfo, setAvailabilityInfo] = useState(null)
  const [cancellationPolicy, setCancellationPolicy] = useState(null)
  const [termsPolicy, setTermsPolicy] = useState(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      cancellationPolicyService.getActive('cancellation').catch(() => null),
      cancellationPolicyService.getActive('terms_and_conditions').catch(() => null),
    ]).then(([c, t]) => {
      if (cancelled) return
      setCancellationPolicy(c?.policy || null)
      setTermsPolicy(t?.policy || null)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Booking form state
  const [bookingData, setBookingData] = useState({
    start_date: '',
    end_date: '',
    num_adults: 1,
    num_children: 0,
    special_requests: '',
    // User details
    name: user?.name || '',
    email: user?.email || '',
    mobile: user?.mobile_number || '',
  })

  useEffect(() => {
    loadService()
  }, [id])

  useEffect(() => {
    if (user) {
      setBookingData((prev) => ({
        ...prev,
        name: user.name || '',
        email: user.email || '',
        mobile: user.mobile_number || '',
      }))
    }
  }, [user])

  // Check availability when dates change
  useEffect(() => {
    const checkAvailability = async () => {
      if (!bookingData.start_date || !bookingData.end_date || !id) return
      try {
        const data = await serviceService.getAvailability(
          id,
          bookingData.start_date,
          bookingData.end_date,
        )
        if (data.availability && data.availability.length > 0) {
          const minAvailable = Math.min(...data.availability.map((a) => a.available))
          setAvailabilityInfo({
            hasCapacity: true,
            minAvailable,
            dates: data.availability,
          })
        } else {
          setAvailabilityInfo(null)
        }
      } catch {
        setAvailabilityInfo(null)
      }
    }
    checkAvailability()
  }, [bookingData.start_date, bookingData.end_date, id])

  const loadService = async () => {
    try {
      setLoading(true)
      const data = await serviceService.getServiceById(id)
      setService(data.service)

      // For single-day services, set end_date same as start_date
      if (data.service.price_unit === 'per_trip' || data.service.price_unit === 'fixed') {
        // Will set dates together
      }

      setLoading(false)
    } catch (error) {
      toast.error('Failed to load service details')
      setLoading(false)
    }
  }

  const calculatePrice = () => {
    return calculateBookingPrice(service, bookingData)
  }

  const calculateDays = () => {
    return calculateBookingDays(bookingData.start_date, bookingData.end_date)
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setBookingData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      }

      // Auto-set end_date for single-day services
      if (
        name === 'start_date' &&
        (service?.price_unit === 'per_trip' || service?.price_unit === 'fixed')
      ) {
        updated.end_date = value
      }

      return updated
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!user) {
      toast.error('Please login to book this service')
      navigate('/login', { state: { from: `/services/${id}/book` } })
      return
    }

    if (!bookingData.start_date || !bookingData.end_date) {
      toast.error('Please select dates')
      return
    }

    const totalPeople = Number(bookingData.num_adults) + Number(bookingData.num_children)
    if (service.capacity && totalPeople > service.capacity) {
      toast.error(`Maximum capacity is ${service.capacity} people`)
      return
    }

    // Validate phone
    const phoneErr = validatePhone(bookingData.mobile)
    if (phoneErr) {
      toast.error(phoneErr)
      return
    }

    setSubmitting(true)
    try {
      const bookingPayload = {
        booking_type: 'service',
        start_date: bookingData.start_date,
        end_date: bookingData.end_date,
        num_adults: parseInt(bookingData.num_adults),
        num_children: parseInt(bookingData.num_children),
        special_requests: bookingData.special_requests,
        services: [{ service_id: parseInt(id), quantity: 1 }],
        service_remarks: serviceRemark
          ? (() => {
              const type =
                service.type === 'car'
                  ? 'car'
                  : service.type === 'guide'
                    ? 'guide'
                    : service.type === 'hotel' || service.type === 'homestay'
                      ? 'hotel'
                      : service.type === 'camp_site'
                        ? 'camp_site'
                        : null
              if (type) return { [type]: serviceRemark }
              if (service.type === 'activity')
                return { activities: [{ service_id: parseInt(id), remarks: serviceRemark }] }
              return null
            })()
          : null,
      }

      const result = await bookingService.createBooking(bookingPayload)
      trackPurchase({
        id: result.booking?.id || result.id,
        item_id: id,
        item_name: service.name,
        total_price: result.booking?.total_price || service.price,
        category: 'service',
      })

      toast.success('Service booked successfully!')
      navigate('/bookings')
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to book service')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader size={80} />
      </div>
    )
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Service not found</h2>
          <Button onClick={() => navigate('/services')}>Browse Services</Button>
        </div>
      </div>
    )
  }

  const totalPrice = calculatePrice()
  const days = calculateDays()

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEO title="Book Service" noindex />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/services')} className="mb-4">
            ← Back to Services
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Book Service</h1>
          <p className="text-gray-600 mt-2">Complete your booking details below</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="md:col-span-2 space-y-6">
            {/* Service Info Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex gap-4">
                {service.image_url && (
                  <img
                    src={service.image_url}
                    alt={service.name}
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{service.name}</h2>
                  <p className="text-gray-600 mb-3">{service.description}</p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-semibold">
                      {service.type.charAt(0).toUpperCase() + service.type.slice(1)}
                    </span>
                    <span className="text-blue-600 font-bold text-lg">
                      {formatPrice(service.base_price)}/{service.price_unit?.replace('_', ' ')}
                    </span>
                    {service.capacity && (
                      <span className="text-gray-500">
                        <FaUsers className="inline mr-1" />
                        Max: {service.capacity}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Date Selection */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FaCalendar className="text-blue-600" />
                  Select Dates
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {service.price_unit === 'per_trip' || service.price_unit === 'fixed'
                        ? 'Date'
                        : 'Start Date'}{' '}
                      *
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      value={bookingData.start_date}
                      onChange={handleInputChange}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {service.price_unit !== 'per_trip' && service.price_unit !== 'fixed' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date *
                      </label>
                      <input
                        type="date"
                        name="end_date"
                        value={bookingData.end_date}
                        onChange={handleInputChange}
                        min={bookingData.start_date || new Date().toISOString().split('T')[0]}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
                {days > 1 && (
                  <p className="text-sm text-gray-600 mt-2">
                    <FaClock className="inline mr-1" />
                    Duration: {days} day{days > 1 ? 's' : ''}
                  </p>
                )}

                {/* Availability Notice */}
                {availabilityInfo && availabilityInfo.hasCapacity && (
                  <div
                    className={`mt-3 p-3 rounded-lg text-sm ${
                      availabilityInfo.minAvailable > 0
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                    }`}
                  >
                    {availabilityInfo.minAvailable > 0 ? (
                      <span>
                        <FaCheck className="inline mr-1" />
                        {availabilityInfo.minAvailable} spot
                        {availabilityInfo.minAvailable !== 1 ? 's' : ''} available
                      </span>
                    ) : (
                      <span>Fully booked for selected dates</span>
                    )}
                  </div>
                )}
              </div>

              {/* Participants */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <FaUsers className="text-blue-600" />
                  Number of Participants
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Adults (18+) *
                    </label>
                    <input
                      type="number"
                      name="num_adults"
                      value={bookingData.num_adults}
                      onChange={handleInputChange}
                      min="1"
                      max={service.capacity || 100}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Children (Under 18)
                    </label>
                    <input
                      type="number"
                      name="num_children"
                      value={bookingData.num_children}
                      onChange={handleInputChange}
                      min="0"
                      max={service.capacity || 100}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                {service.capacity && (
                  <p className="text-sm text-gray-500 mt-2">
                    Total: {Number(bookingData.num_adults) + Number(bookingData.num_children)} /{' '}
                    {service.capacity} (Maximum capacity)
                  </p>
                )}
              </div>

              {/* Contact Details */}
              {!user && (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold mb-4">Contact Details</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={bookingData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={bookingData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mobile *
                      </label>
                      <input
                        type="tel"
                        name="mobile"
                        value={bookingData.mobile}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Service Remark */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4">Service Remark (Optional)</h3>
                <input
                  type="text"
                  placeholder="Add a remark for this service (e.g., specific preferences)"
                  value={serviceRemark}
                  onChange={(e) => setServiceRemark(e.target.value)}
                  maxLength={500}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">{serviceRemark.length}/500</p>
              </div>

              {/* Special Requests */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-bold mb-4">Special Requests (Optional)</h3>
                <textarea
                  name="special_requests"
                  value={bookingData.special_requests}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Any special requirements or preferences..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </form>
          </div>

          {/* Booking Summary Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-lg sticky top-8">
              <h3 className="text-xl font-bold mb-4">Booking Summary</h3>

              <div className="space-y-3 mb-6 pb-6 border-b">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Service</span>
                  <span className="font-semibold">{service.name}</span>
                </div>

                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Base Price</span>
                  <span>{formatPrice(service.base_price)}</span>
                </div>

                {days > 1 && service.price_unit === 'per_day' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Days × {days}</span>
                    <span>{formatPrice(service.base_price * days)}</span>
                  </div>
                )}

                {service.price_unit === 'per_person' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Adults × {bookingData.num_adults}</span>
                    <span>
                      {formatPrice(Number(service.base_price) * Number(bookingData.num_adults))}
                    </span>
                  </div>
                )}

                {service.price_unit === 'per_person' && bookingData.num_children > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Children × {bookingData.num_children}</span>
                    <span>
                      {formatPrice(Number(service.base_price) * Number(bookingData.num_children))}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center mb-6 text-lg font-bold">
                <span>Total Amount</span>
                <span className="text-blue-600">{formatPrice(totalPrice)}</span>
              </div>

              <Button
                type="submit"
                onClick={handleSubmit}
                disabled={!bookingData.start_date || !bookingData.end_date}
                loading={submitting}
                fullWidth
              >
                Confirm Booking
              </Button>

              <div className="mt-4 space-y-2">
                <PolicyDisplay policy={cancellationPolicy} title="Cancellation Policy" />
                <PolicyDisplay policy={termsPolicy} title="Terms & Conditions" />
              </div>

              <div className="mt-4 space-y-2 text-xs text-gray-500">
                <div className="flex items-center gap-2">
                  <FaCheck className="text-green-500" />
                  <span>Instant confirmation</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheck className="text-green-500" />
                  <span>Free cancellation up to 48 hours</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaCheck className="text-green-500" />
                  <span>Secure payment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ServiceBooking
